import React, { useContext } from 'react'
import { ComponentConfig, renderContext } from '@puckeditor/core'
import { withStyleFields } from '../StyleWrapper'
import { withLayout } from '../Layout'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  applyStyleProps,
} from '../../fields/StyleFields'
import * as styles from './VideoEmbed.module.scss'

type VideoProvider = 'youtube' | 'vimeo' | 'wistia'

interface ParsedVideo {
  provider: VideoProvider
  embedUrl: string
}

function parseVideoUrl(url: string): ParsedVideo | null {
  if (!url) return null

  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/,
  )
  if (ytMatch) {
    return {
      provider: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`,
    }
  }

  // Vimeo: vimeo.com/ID, player.vimeo.com/video/ID
  const vimeoMatch = url.match(
    /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/,
  )
  if (vimeoMatch) {
    return {
      provider: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
    }
  }

  // Wistia: *.wistia.com/medias/ID, fast.wistia.com/embed/iframe/ID
  const wistiaMatch = url.match(
    /wistia\.com\/(?:medias|embed\/iframe)\/([\w-]+)/,
  )
  if (wistiaMatch) {
    return {
      provider: 'wistia',
      embedUrl: `https://fast.wistia.com/embed/iframe/${wistiaMatch[1]}`,
    }
  }

  // Bare Wistia video ID (alphanumeric hash, e.g. "abc123def")
  const bareWistiaMatch = url.match(/^[\w-]+$/)
  if (bareWistiaMatch) {
    return {
      provider: 'wistia',
      embedUrl: `https://fast.wistia.com/embed/iframe/${url}`,
    }
  }

  return null
}

const ASPECT_RATIOS: Record<string, string> = {
  '16:9': '16 / 9',
  '4:3': '4 / 3',
  '1:1': '1 / 1',
}

export type VideoEmbedProps = {
  url: string
  aspectRatio: '16:9' | '4:3' | '1:1'
  maxWidth?: string
  params?: string
  background?: BackgroundStyleProps
  border?: BorderStyleProps
}

const VideoEmbedRender = ({
  url,
  aspectRatio,
  maxWidth,
  params,
  background,
  border,
}: VideoEmbedProps) => {
  const puckCtx = useContext(renderContext)
  const isRenderMode = puckCtx.data.content.length > 0
  const styleProps = applyStyleProps({ background, border })

  if (!url) {
    return (
      <div
        className={styles.placeholder}
        style={{ maxWidth: maxWidth || '100%', ...styleProps }}
      >
        Paste a video URL to embed
      </div>
    )
  }

  const parsed = parseVideoUrl(url)

  if (!parsed) {
    return (
      <div className={styles.error} style={{ maxWidth: maxWidth || '100%', ...styleProps }}>
        Unsupported video URL. Use YouTube, Vimeo, or Wistia ID.
      </div>
    )
  }

  const aspectRatioValue = ASPECT_RATIOS[aspectRatio] || ASPECT_RATIOS['16:9']
  const embedSrc = params
    ? `${parsed.embedUrl}${parsed.embedUrl.includes('?') ? '&' : '?'}${params}`
    : parsed.embedUrl

  // isolation:isolate forces the wrapper into its own compositing layer, which
  // makes overflow:hidden actually clip iframe content to the border-radius.
  const wrapperStyle: React.CSSProperties = {
    maxWidth: maxWidth || '100%',
    ...styleProps,
    ...(border?.borderRadius ? { overflow: 'hidden', isolation: 'isolate' } : {}),
  }

  if (!isRenderMode) {
    return (
      <div className={styles.editorWrapper} style={wrapperStyle}>
        <iframe
          src={embedSrc}
          title="Video embed"
          className={styles.editorIframe}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    )
  }

  return (
    <div className={styles.wrapper} style={wrapperStyle}>
      <iframe
        src={embedSrc}
        title="Video embed"
        className={styles.iframe}
        style={{ aspectRatio: aspectRatioValue }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
}

// withLayout adds padding/margin/size on a wrapper (BR-5406); style fields
// stay on the inner withStyleFields so border-radius keeps clipping the iframe.
export const PuckVideoEmbed = withLayout(
  withStyleFields<ComponentConfig<VideoEmbedProps>>({
  label: 'Video Embed',
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description: 'Responsive YouTube, Vimeo or Wistia player.',
    fields: {
      url: 'Full YouTube or Vimeo URL, or a Wistia media id.',
      params: 'Query string for the player, e.g. autoplay=1&muted=1.',
    },
  },
  defaultProps: { url: '', aspectRatio: '16:9', maxWidth: '100%', params: '' },
  fields: {
    url: { type: 'text', label: 'Video URL or Wistia ID' },
    aspectRatio: {
      type: 'radio',
      label: 'Aspect Ratio',
      options: [
        { label: '16:9', value: '16:9' },
        { label: '4:3', value: '4:3' },
        { label: '1:1', value: '1:1' },
      ],
    },
    maxWidth: { type: 'text', label: 'Max Width (e.g. 600px)' },
    params: {
      type: 'text',
      label: 'Embed Parameters (e.g. autoplay=1&muted=1)',
    },
  },
    render: props => <VideoEmbedRender {...props} />,
  }),
  { disableStyleFields: true },
)
